import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { 
  getDataProvider, 
  subscribeToTable,
  type Subscription,
  type ChangePayload
} from '@/lib/dataProvider'
import { useAuthStore } from './auth'

import { logger } from '@/lib/logger'
// TypeScript interfaces based on the database schema
export interface Display {
  id: string
  user_id: string
  name: string
  description: string | null
  is_active: boolean
  position_x: number
  position_y: number
  width: number
  height: number
  created_at: string
  updated_at: string
}

export interface CreateDisplayData {
  name: string
  description?: string
  position_x?: number
  position_y?: number
  width?: number
  height?: number
}

export interface UpdateDisplayData {
  name?: string
  description?: string | null
  position_x?: number
  position_y?: number
  width?: number
  height?: number
  is_active?: boolean
}

export const useDisplaysStore = defineStore('displays', () => {
  // State
  const displays = ref<Map<string, Display>>(new Map())
  const loadingStates = ref<Map<string, boolean>>(new Map())
  const error = ref<string | null>(null)
  
  // Subscription handle
  let realtimeSubscription: Subscription | null = null

  // Getters
  const allDisplays = computed(() => {
    return Array.from(displays.value.values())
      .sort((a, b) => a.name.localeCompare(b.name))
  })

  const activeDisplays = computed(() => {
    return allDisplays.value.filter(display => display.is_active)
  })

  const isLoading = computed(() => (key: string) => {
    return loadingStates.value.get(key) || false
  })

  // Helper functions
  const setLoading = (key: string, loading: boolean) => {
    loadingStates.value.set(key, loading)
  }

  const setError = (errorMessage: string | null) => {
    error.value = errorMessage
  }

  const addDisplay = (display: Display) => {
    displays.value.set(display.id, display)
  }

  const removeDisplay = (displayId: string) => {
    displays.value.delete(displayId)
  }

  const updateDisplay = (displayId: string, updates: Partial<Display>) => {
    const display = displays.value.get(displayId)
    if (display) {
      displays.value.set(displayId, { ...display, ...updates })
    }
  }

  // Actions
  const fetchUserDisplays = async () => {
    const provider = getDataProvider()
    
    // In standalone mode, user is always "authenticated"
    const userId = provider.getCurrentUserId()
    if (!userId && provider.getMode() === 'online') {
      setError('User not authenticated')
      return { success: false, error: 'User not authenticated' }
    }

    setLoading('fetchDisplays', true)
    setError(null)

    try {
      const data = await provider.query<Display>('displays', {
        orderBy: [{ column: 'name', ascending: true }]
      })

      // Clear existing displays and add new ones
      displays.value.clear()
      data.forEach(display => addDisplay(display))

      return { success: true, data }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch displays'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading('fetchDisplays', false)
    }
  }

  const createDisplay = async (displayData: CreateDisplayData) => {
    logger.info('DisplaysStore: createDisplay called with:', displayData)
    
    const provider = getDataProvider()
    const userId = provider.getCurrentUserId()
    
    logger.info('DisplaysStore: User ID:', userId)
    
    if (!userId && provider.getMode() === 'online') {
      logger.info('DisplaysStore: User not authenticated')
      return { success: false, error: 'User not authenticated' }
    }

    setLoading('createDisplay', true)
    setError(null)

    try {
      logger.info('DisplaysStore: Inserting display into database')
      
      const result = await provider.insert<Display>('displays', {
        user_id: userId,
        name: displayData.name,
        description: displayData.description ?? null,
        position_x: displayData.position_x ?? 0,
        position_y: displayData.position_y ?? 0,
        width: displayData.width ?? 1920,
        height: displayData.height ?? 1080,
        is_active: true
      } as Partial<Display>)

      logger.info('DisplaysStore: Database response:', result)

      if (!result.success) {
        throw new Error(result.error)
      }
      
      if (!result.data) {
        throw new Error('No data returned from display creation')
      }

      // Add to local state
      logger.info('DisplaysStore: Adding display to local state')
      addDisplay(result.data)

      logger.info('DisplaysStore: Create display success')
      return { success: true, data: result.data }
    } catch (err) {
      logger.info('DisplaysStore: Create display error:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to create display'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading('createDisplay', false)
    }
  }

  const updateDisplayById = async (displayId: string, updates: UpdateDisplayData) => {
    const provider = getDataProvider()
    const userId = provider.getCurrentUserId()
    
    if (!userId && provider.getMode() === 'online') {
      return { success: false, error: 'User not authenticated' }
    }

    setLoading(`updateDisplay-${displayId}`, true)
    setError(null)

    try {
      const result = await provider.update<Display>('displays', displayId, updates)

      if (!result.success) {
        throw new Error(result.error)
      }
      
      if (!result.data) {
        throw new Error('No data returned from display update')
      }

      // Update local state
      updateDisplay(displayId, result.data)

      return { success: true, data: result.data }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update display'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(`updateDisplay-${displayId}`, false)
    }
  }

  const deleteDisplay = async (displayId: string) => {
    const provider = getDataProvider()
    const userId = provider.getCurrentUserId()
    
    if (!userId && provider.getMode() === 'online') {
      return { success: false, error: 'User not authenticated' }
    }

    setLoading(`deleteDisplay-${displayId}`, true)
    setError(null)

    try {
      const result = await provider.delete('displays', displayId)

      if (!result.success) {
        throw new Error(result.error)
      }

      // Remove from local state
      removeDisplay(displayId)

      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete display'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(`deleteDisplay-${displayId}`, false)
    }
  }

  const subscribeToDisplayUpdates = () => {
    const provider = getDataProvider()
    const userId = provider.getCurrentUserId()
    
    if (!userId) return null

    // Subscribe to changes using the data provider abstraction
    realtimeSubscription = subscribeToTable<Display>(
      'displays',
      handleDisplayChange,
      userId
    )

    return realtimeSubscription
  }

  const handleDisplayChange = (payload: ChangePayload<Display>) => {
    const { eventType, new: newRecord, old: oldRecord } = payload

    switch (eventType) {
      case 'INSERT':
        if (newRecord) {
          addDisplay(newRecord)
        }
        break
      
      case 'UPDATE':
        if (newRecord) {
          updateDisplay(newRecord.id, newRecord)
        }
        break
      
      case 'DELETE':
        if (oldRecord && oldRecord.id) {
          removeDisplay(oldRecord.id)
        }
        break
    }
  }

  const unsubscribeFromUpdates = (subscription?: Subscription | null) => {
    const sub = subscription || realtimeSubscription
    if (sub) {
      sub.unsubscribe()
      if (sub === realtimeSubscription) {
        realtimeSubscription = null
      }
    }
  }

  return {
    // State
    displays,
    error,
    
    // Getters
    allDisplays,
    activeDisplays,
    isLoading,
    
    // Actions
    fetchUserDisplays,
    createDisplay,
    updateDisplayById,
    deleteDisplay,
    subscribeToDisplayUpdates,
    unsubscribeFromUpdates
  }
})